import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Eye, ListRestart, Plus, Search, SquarePen, Trash2 } from "lucide-react";
import RawMaterialSearchDrawer from "./RawMaterialSearchDrawer";
import Pagination from "../../utils/pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { deleteRawMaterial } from "../../../../redux/api/admin/rawMaterialApi";
import { deleteRawMaterialAction, setFilteredStatus } from "../../../../redux/reduxSlice/admin/RawMaterialSlice";
import { formatDateTime, formatMaterialType } from "../../../../../utils/commonFunctions";
import { getRawMaterialList } from "../../../../redux/reduxApi/admin/RawMaterialApi";
const ITEMS_PER_PAGE = 100;

export default function RawMaterialList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [rawMaterialsData, setRawMaterialsData] = useState([]);
  console.log("raw material data", rawMaterialsData);
  const isFiltered = useSelector((state) => state.rawMaterial.isFiltered);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false); // New state for page entry

  const SEARCH_QUERY_KEY = "rawMaterialSearchQuery";
  const [imgErrors, setImgErrors] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);

  const getTotalFromPayload = (payload, fallbackLength) => {
    return payload?.Total || payload?.total || payload?.count || fallbackLength;
  };

  const parseImageUrls = (media) => {
    if (!media) return [];

    try {
      if (Array.isArray(media)) {
        return media
          .map((item) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object" && item.url) return item.url;
            if (item && typeof item === "object" && item.filePath) return item.filePath;
            return null;
          })
          .filter((url) => url && typeof url === "string" && url.trim().startsWith("http"));
      }

      if (typeof media === "string") {
        if (media.startsWith("[") && media.endsWith("]")) {
          try {
            const parsed = JSON.parse(media);
            return parseImageUrls(parsed);
          } catch (error) {
            console.error("Raw material image JSON parse error:", error);
          }
        }

        const urlRegex = /https?:\/\/[^\s,}\]]+/g;
        return media.match(urlRegex) || [];
      }

      return [];
    } catch (error) {
      console.error("Error parsing raw material image URLs:", error);
      return [];
    }
  };

  const getFirstImageUrl = (item) => {
    const images = parseImageUrls(item?.media || item?.img);
    return images.length > 0 ? images[0] : "";
  };

  const getItemsText = (items, key) => {
    if (!Array.isArray(items) || items.length === 0) return "N/A";

    const values = items.map((item) => item?.[key]).filter(Boolean);
    const uniqueValues = [...new Set(values)];
    return uniqueValues.length > 0 ? uniqueValues.join(", ") : "N/A";
  };

  const getTableData = (item) => ({
    ROWID: item.ROWID,
    productName: item.productName,
materialType: formatMaterialType(item.materialType?.materialType),
    consumptionUnit: item.consumptionUnit || "N/A",
    available: item.available || "0",
    sku: item.sku,
    purchasePrice: item.purchasePrice,
    hsnCode: item.hsnCode,
    tax: item.tax,
    colour: item.colour?.colour || "N/A",
    vendor: getItemsText(item.items, "vender"),
    vendorShadeNo: getItemsText(item.items, "venderShadeNo"),
    vendorPurchasePrice: getItemsText(item.items, "purchasePrize"),
    createdTime: item.CREATEDTIME,
    // createdBy: item.CREATORID,
    imageUrl: getFirstImageUrl(item),
  });

  const currentItems = rawMaterialsData;

  const openTrash = () => setIsOpen(true);
  const closeTrash = () => {
    setIsOpen(false);
    setDeleteMaterialId(null);
  };
  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  const fetchRawMaterials = useCallback((page, searchQuery = null) => {
    const payload = searchQuery
      ? { ...searchQuery, page, size: ITEMS_PER_PAGE }
      : { page, size: ITEMS_PER_PAGE };
    setPageLoading(true)

    dispatch(getRawMaterialList(payload))
      .then((res) => {
        console.log("Raw material list response:", res);
        const data = res.payload?.data || [];
        setRawMaterialsData(Array.isArray(data) ? data : []);
        const total = res.payload?.Total || data.length;
        setRecordCount(total);
        setPageLoading(false)

      })
      .catch((err) => {
        console.error("Error fetching raw material data:", err);
        toast({
          title: "Error fetching raw materials",
          description: err.message || "Please try again",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setRawMaterialsData([]);
        setRecordCount(0);
        setPageLoading(false)
      });
  }, [dispatch, toast]);

  // Fetch data on page change or when filtered state changes
  useEffect(() => {
    const savedQuery = sessionStorage.getItem(SEARCH_QUERY_KEY);

    if (savedQuery && isFiltered) {
      const queryPayload = JSON.parse(savedQuery);
      fetchRawMaterials(currentPage, queryPayload);
    } else {
      fetchRawMaterials(currentPage);
    }
  }, [currentPage, dispatch, isFiltered, fetchRawMaterials]);

  const handleImageError = (id) => {
    setImgErrors((prev) => ({
      ...prev,
      [id]: true,
    }));
  };

  const handleRowSelect = (id) => {
    const isSelected = selectedRows.includes(id);
    const updatedRows = isSelected
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];
    setSelectedRows(updatedRows);
  };

  const handleSelectAllRows = () => {
    if (selectedRows.length === currentItems.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentItems.map((item) => item.ROWID));
    }
  };

  const handleDelete = async () => {
    const idsToDelete = deleteMaterialId ? [deleteMaterialId] : selectedRows;

    if (idsToDelete.length === 0) {
      closeTrash();
      return;
    }

    setIsDeleting(true);

    try {
      // Delete each selected item
      const deletePromises = idsToDelete.map(id => deleteRawMaterial(id));
      await Promise.all(deletePromises);

      // Dispatch Redux action to update state
      dispatch(deleteRawMaterialAction(idsToDelete));

      // Update local state
      const updatedData = rawMaterialsData.filter((item) => !idsToDelete.includes(item.ROWID));
      setRawMaterialsData(updatedData);
      setRecordCount(updatedData.length);
      setSelectedRows([]);
      setDeleteMaterialId(null);

      toast({
        title: "Success",
        description: `${idsToDelete.length} raw material(s) deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh if current page becomes empty
      if (updatedData.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchRawMaterials(currentPage);
      }
    } catch (error) {
      console.error("Error deleting raw materials:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete raw material(s)",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      closeTrash();
    }
  };

  const handleReset = () => {
    dispatch(setFilteredStatus(false));
    sessionStorage.removeItem(SEARCH_QUERY_KEY);
    setSelectedRows([]);
    setCurrentPage(1);
    fetchRawMaterials(1);
  };

  return (
    <>
      {pageLoading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>
      )}
      <Modal isOpen={isOpen} onClose={closeTrash}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRows.length > 0
              ? `You are about to delete ${selectedRows.length} raw material(s). This action cannot be undone.`
              : "This action cannot be undone."}
          </ModalBody>
          <ModalFooter>
            <Button
              className="main-btn"
              mr={3}
              onClick={handleDelete}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeTrash}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="min-h-[85vh] h-[97vh] relative">
        <div className="d-flex justify-content-between mb-1 px-2 items-center">
          <div>
            {selectedRows.length > 0 && (
              <button className="main-btn mx-2" onClick={openTrash}>
                <span className="inner-content">
                  Delete All ({selectedRows.length})
                </span>
              </button>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <button
              className="main-btn"
              onClick={isFiltered ? handleReset : openSearch}
            >
              <span className="inner-content">
                {isFiltered ? (
                  <>
                    <ListRestart size="18px" />
                    <span className="ms-1">Show All</span>
                  </>
                ) : (
                  <>
                    <Search size="18px" />
                    <span className="ms-1">Search</span>
                  </>
                )}
              </span>
            </button>
            <Link to="/admin/inventory/raw-material-form">
              <button className="d-flex justify-content-between align-items-center main-btn">
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Add Raw Material</span>
                </span>
              </button>
            </Link>
          </div>
        </div>

        <div className="flex-grow px-1">
          <TableContainer
            style={{
              height: "calc(97vh - 135px)",
              background: "#fff",
              border: "1px solid #e7e7e7",
              overflow: "auto",
            }}
          >
            <Table size="sm">
              <Thead position="sticky" top={0} bg="white" zIndex={10}>
                <Tr>
                  <Th>
                    <Checkbox
                      isChecked={
                        currentItems.length > 0 &&
                        selectedRows.length === currentItems.length
                      }
                      isDisabled={currentItems.length === 0}
                      onChange={handleSelectAllRows}
                    />
                  </Th>
                  <Th>Actions</Th>
                  <Th>Image</Th>
                  <Th>Material Name</Th>
                  <Th>Material Type</Th>
                  <Th>SKU</Th>
                  <Th>Available Stock</Th>
                  <Th>Consumption Unit</Th>
                  <Th>Purchase Price</Th>
                  <Th>HSN Code</Th>
                  <Th>Tax</Th>
                  <Th>Colour</Th>
                  <Th>Created Time</Th>
                </Tr>
              </Thead>

              <Tbody>
                {rawMaterialsData.length > 0 ? (
                  rawMaterialsData.map((item) => {
                    const tableData = getTableData(item);

                    return (
                      <Tr key={item.ROWID}>
                        <Td>
                          <Checkbox
                            isChecked={selectedRows.includes(item.ROWID)}
                            onChange={() => handleRowSelect(item.ROWID)}
                          />
                        </Td>

                        <Td>
                          <div className="flex gap-1">
                            <Eye
                              size={16}
                              onClick={() =>
                                navigate("/admin/inventory/raw-material-detail", {
                                  state: { id: item.ROWID },
                                })
                              }
                              cursor="pointer"
                              title="View"
                            />
                            <SquarePen
                              size={16}
                              cursor="pointer"
                              onClick={() =>
                                navigate("/admin/inventory/raw-material-form-update", {
                                  state: {
                                    isUpdateMode: true,
                                    id: item.ROWID,
                                  },
                                })
                              }
                              title="Edit"
                            />
                            <Trash2
                              size={16}
                              cursor="pointer"
                              onClick={() => {
                                setDeleteMaterialId(item.ROWID);
                                openTrash();
                              }}
                              title="Delete"
                            />
                          </div>
                        </Td>

                        <Td>
                          <div style={{ height: "40px", width: "40px" }}>
                            {tableData.imageUrl && !imgErrors[tableData.ROWID] ? (
                              <img
                                src={tableData.imageUrl}
                                alt={`Raw material ${tableData.productName || ""}`}
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                                onError={() => handleImageError(tableData.ROWID)}
                              />
                            ) : (
                              <div
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  backgroundColor: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                }}
                              >
                                No Image
                              </div>
                            )}
                          </div>
                        </Td>

                        <Td>{tableData.productName || "N/A"}</Td>
                        <Td>{tableData.materialType || "N/A"}</Td>
                        <Td>{tableData.sku || "N/A"}</Td>
                        <Td>{tableData.available || 0}</Td>
                        <Td>{tableData.consumptionUnit || "N/A"}</Td>
                        <Td>{tableData.purchasePrice || "N/A"}</Td>
                        <Td>{tableData.hsnCode || "N/A"}</Td>
                        <Td>{tableData.tax || "N/A"}</Td>
                        <Td>{tableData.colour}</Td>
                        <Td>{formatDateTime(tableData.createdTime)}</Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={10} style={{ textAlign: "center", padding: "20px" }}>
                      No raw materials found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        <div
          className="w-100 bg-white shadow-sm"
          style={{ position: "sticky", bottom: 0, zIndex: 20 }}
        >
          <div className="row mx-2 flex items-center">
            <div className="col-lg-3 col-md-6 col-12">
              <p className="fw-semibold mb-0">Total Count: {recordCount}</p>
            </div>
            <div className="col-lg-6 col-md-6 col-12 flex justify-center">
              <Pagination
                totalPages={Math.ceil((recordCount || 0) / ITEMS_PER_PAGE) || 1}
                onPageChange={setCurrentPage}
                pageCount={currentPage}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-12"></div>
          </div>
        </div>
      </div>

      <RawMaterialSearchDrawer
        closeDrawer={closeSearch}
        isSearchDrawerOpen={isSearchOpen}
        setRecordCount={setRecordCount}
      />
    </>
  );
}